import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Tooltip,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../hooks/useLocalization';

const Levels = () => {
  const { t } = useLocalization();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    level_name: '',
    points_count: ''
  });
  const { user } = useAuth();

  const fetchLevels = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/levels');
      setLevels(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_fetch_levels'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const handleOpenDialog = (level = null) => {
    if (level) {
      setEditingLevel(level);
      setFormData({
        level_name: level.level_name,
        points_count: level.points_count.toString()
      });
    } else {
      setEditingLevel(null);
      setFormData({
        level_name: '',
        points_count: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLevel(null);
    setFormData({
      level_name: '',
      points_count: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        level_name: formData.level_name,
        points_count: parseInt(formData.points_count)
      };

      if (editingLevel) {
        await axios.put(`/levels/${editingLevel.level_id}`, data);
      } else {
        await axios.post('/levels', data);
      }

      handleCloseDialog();
      fetchLevels();
      
      // Dispatch event to notify about level changes
      window.dispatchEvent(new CustomEvent('levelsChanged'));
    } catch (err) {
      setError(err.response?.data?.detail || t('failed_to_save_level'));
    }
  };

  const handleDelete = async (levelId) => {
    if (window.confirm(t('confirm_delete_level'))) {
      try {
        await axios.delete(`/levels/${levelId}`);
        fetchLevels();
        
        // Dispatch event to notify about level changes
        window.dispatchEvent(new CustomEvent('levelsChanged'));
      } catch (err) {
        setError(err.response?.data?.detail || t('failed_to_delete_level'));
      }
    }
  };

  const getLevelColor = (points) => {
    if (points === 0) return 'success';
    if (points <= 100) return 'primary';
    if (points <= 500) return 'warning';
    return 'error';
  };

  const getLevelDifficulty = (points) => {
    if (points === 0) return t('difficulty_beginner');
    if (points <= 100) return t('difficulty_easy');
    if (points <= 500) return t('difficulty_medium');
    return t('difficulty_hard');
  };

  const columns = [
    { 
      field: 'level_name', 
      headerName: t('level_name'), 
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ 
            width: 32, 
            height: 32, 
            mr: 2, 
            bgcolor: getLevelColor(params.row.points_count) + '.main'
          }}>
            <EmojiEventsIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'points_count', 
      headerName: t('points_required'), 
      width: 200,
      renderCell: (params) => (
        <Chip
          label={`${params.value} ${t('points_text')}`}
          color={getLevelColor(params.value)}
          variant="filled"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'difficulty',
      headerName: t('difficulty'),
      width: 150,
      valueGetter: (params) => getLevelDifficulty(params.row.points_count),
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getLevelColor(params.row.points_count)}
          variant="outlined"
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('edit_level')}>
            <IconButton 
              onClick={() => handleOpenDialog(params.row)}
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.light',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete_level')}>
            <IconButton 
              onClick={() => handleDelete(params.row.level_id)}
              sx={{ 
                color: 'error.main',
                '&:hover': {
                  bgcolor: 'error.light',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <EmojiEventsIcon sx={{ mr: 2, fontSize: 'inherit' }} />
          {t('levels_management')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300 }}>
          {t('configure_user_levels_progression')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <EmojiEventsIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {levels.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('total_levels')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {t('level_configuration')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('manage_user_progression_levels')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('add_level')}
            </Button>
          </Box>
          
          <Paper sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
            <DataGrid
              rows={levels}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              getRowId={(row) => row.points_count}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          </Paper>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {editingLevel ? t('edit_level_dialog') : t('add_new_level')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ mt: 2, minWidth: '400px' }}>
              <TextField
                autoFocus
                label={t('level_name')}
                fullWidth
                value={formData.level_name}
                onChange={(e) => setFormData({ ...formData, level_name: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                helperText={t('level_name_placeholder')}
              />
              <TextField
                label={t('points_required')}
                type="number"
                fullWidth
                value={formData.points_count}
                onChange={(e) => setFormData({ ...formData, points_count: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                helperText={t('points_required_placeholder')}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              {t('cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              {editingLevel ? t('update_level') : t('add_level_button')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Levels; 